import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';

export default function Sidebar({ onClose, isMobile }) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [editingVisibility, setEditingVisibility] = useState(false);
  const [hiddenClients, setHiddenClients] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_hidden') || '[]'); }
    catch { return []; }
  });

  useEffect(() => { loadClients(); }, []);

  async function loadClients() {
    const { data } = await supabase
      .from('clients')
      .select('id, name, ad_accounts(platform)')
      .order('name');
    if (data) setClients(data);
  }

  function toggleHidden(id) {
    const next = hiddenClients.includes(id)
      ? hiddenClients.filter(x => x !== id)
      : [...hiddenClients, id];
    setHiddenClients(next);
    localStorage.setItem('sidebar_hidden', JSON.stringify(next));
  }

  function getUniquePlatforms(adAccounts) {
    return [...new Set((adAccounts || []).map(a => a.platform))];
  }

  function getInitials(name) {
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  }

  const activeClientId = new URLSearchParams(location.search).get('client');
  const visibleClients = editingVisibility ? clients : clients.filter(c => !hiddenClients.includes(c.id));

  const isDark = theme === 'dark';

  // Colori flat coerenti col nuovo ReportTemplate
  const colors = {
    bg: isDark ? '#18181b' : '#ffffff',
    border: isDark ? '#27272a' : '#e5e5e5',
    text: isDark ? '#fafafa' : '#1a1a1a',
    text2: isDark ? '#a1a1aa' : '#52525b',
    text3: isDark ? '#71717a' : '#a1a1aa',
    surface: isDark ? '#27272a' : '#f5f5f3',
    activeText: '#7F77DD',
    activeBg: isDark ? '#1e1b2e' : '#EEEDFE',
    hoverBg: isDark ? '#27272a' : '#f5f5f3',
    meta: { bg: 'rgba(24,119,242,0.10)', color: '#1877f2' },
    google: { bg: 'rgba(52,168,83,0.10)', color: '#34a853' },
    tiktok: { bg: isDark ? '#27272a' : '#f0f0ee', color: isDark ? '#a1a1aa' : '#71717a' },
  };

  function handleClientClick(id) {
    navigate(`/?client=${id}`);
    if (isMobile && onClose) onClose();
  }

  function handleNavClick() {
    if (isMobile && onClose) onClose();
  }

  const navLinkStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    color: isActive ? colors.activeText : colors.text2,
    background: isActive ? colors.activeBg : 'transparent',
    transition: 'background 0.15s',
    position: 'relative',
  });

  return (
    <div style={{
      width: 220,
      background: colors.bg,
      borderRight: `1px solid ${colors.border}`,
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      height: '100vh',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
    }}>

      {/* Header: logo + azioni */}
      <div style={{
        padding: '20px 16px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderBottom: `1px solid ${colors.border}`,
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 20, fontWeight: 600, color: '#7F77DD', lineHeight: 1 }}>G</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: colors.text, flex: 1 }}>Gallicantus</span>

        <button onClick={toggleTheme} title={isDark ? 'Tema chiaro' : 'Tema scuro'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text3, padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isDark
            ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7" cy="7" r="3"/><line x1="7" y1="1" x2="7" y2="2.5"/><line x1="7" y1="11.5" x2="7" y2="13"/><line x1="1" y1="7" x2="2.5" y2="7"/><line x1="11.5" y1="7" x2="13" y2="7"/><line x1="2.9" y1="2.9" x2="4" y2="4"/><line x1="10" y1="10" x2="11.1" y2="11.1"/><line x1="11.1" y1="2.9" x2="10" y2="4"/><line x1="4" y1="10" x2="2.9" y2="11.1"/></svg>
            : <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M11.5 8.5A5 5 0 015.5 2.5a5 5 0 106 6z" strokeLinecap="round"/></svg>
          }
        </button>

        {isMobile && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text3, padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>
          </button>
        )}
      </div>

      {/* Nav principale */}
      <div style={{ padding: '12px 10px 0', display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
        <NavLink to="/" end style={({ isActive }) => navLinkStyle(isActive)} onClick={handleNavClick}
          onMouseEnter={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = colors.hoverBg; }}
          onMouseLeave={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = ''; }}
        >
          {({ isActive }) => <>
            {isActive && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 14, borderRadius: 2, background: '#7F77DD' }} />}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" style={{ flexShrink: 0 }}>
              <rect x="1" y="8" width="3" height="5" rx="0.8" fill={isActive ? '#7F77DD' : 'currentColor'} stroke="none"/>
              <rect x="5.5" y="5" width="3" height="8" rx="0.8" fill={isActive ? '#7F77DD' : 'currentColor'} stroke="none" opacity="0.6"/>
              <rect x="10" y="1.5" width="3" height="11.5" rx="0.8" fill={isActive ? '#7F77DD' : 'currentColor'} stroke="none" opacity="0.35"/>
            </svg>
            <span>Genera Report</span>
          </>}
        </NavLink>

        <NavLink to="/reports" style={({ isActive }) => navLinkStyle(isActive)} onClick={handleNavClick}
          onMouseEnter={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = colors.hoverBg; }}
          onMouseLeave={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = ''; }}
        >
          {({ isActive }) => <>
            {isActive && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 14, borderRadius: 2, background: '#7F77DD' }} />}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={isActive ? '#7F77DD' : 'currentColor'} strokeWidth="1.3" style={{ flexShrink: 0 }}>
              <rect x="2" y="1.5" width="10" height="11" rx="1.5"/>
              <line x1="4.5" y1="4.5" x2="9.5" y2="4.5"/>
              <line x1="4.5" y1="7" x2="9.5" y2="7"/>
              <line x1="4.5" y1="9.5" x2="7.5" y2="9.5"/>
            </svg>
            <span>Report</span>
          </>}
        </NavLink>
        <NavLink to="/data-health" style={({ isActive }) => navLinkStyle(isActive)} onClick={handleNavClick}
          onMouseEnter={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = colors.hoverBg; }}
          onMouseLeave={e => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = ''; }}
        >
          {({ isActive }) => <>
            {isActive && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 14, borderRadius: 2, background: '#7F77DD' }} />}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={isActive ? '#7F77DD' : 'currentColor'} strokeWidth="1.3" style={{ flexShrink: 0 }}>
              <path d="M7 1.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11z"/>
              <path d="M4.5 7l1.5 1.5L9.5 5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Data Health</span>
          </>}
        </NavLink>
      </div>

      {/* Clienti */}
      <div style={{ marginTop: 20, flex: 1, overflow: 'auto', minHeight: 0 }}>
        <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: colors.text3 }}>
            Clienti{hiddenClients.length > 0 && !editingVisibility && (
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 10, marginLeft: 4 }}>
                ({clients.length - hiddenClients.length}/{clients.length})
              </span>
            )}
          </span>
          <button
            onClick={() => setEditingVisibility(v => !v)}
            title={editingVisibility ? 'Chiudi' : 'Mostra/nascondi clienti'}
            style={{
              background: editingVisibility ? colors.activeBg : 'none',
              border: 'none',
              cursor: 'pointer',
              color: editingVisibility ? colors.activeText : colors.text3,
              padding: '2px 4px',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.3">
              {editingVisibility
                ? <><line x1="2" y1="2" x2="11" y2="11"/><line x1="11" y1="2" x2="2" y2="11"/></>
                : <><circle cx="6.5" cy="6.5" r="2"/><path d="M1 6.5C2.5 3.5 4.8 2 6.5 2s4 1.5 5.5 4.5C10.5 9.5 8.2 11 6.5 11S2.5 9.5 1 6.5z"/></>
              }
            </svg>
          </button>
        </div>

        {editingVisibility && (
          <div style={{ margin: '0 10px 8px', padding: '8px 10px', background: colors.surface, borderRadius: 6, border: `1px solid ${colors.border}` }}>
            <p style={{ fontSize: 11, color: colors.text2, margin: 0, lineHeight: 1.4 }}>
              Clicca un cliente per mostrarlo o nasconderlo dalla sidebar.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visibleClients.map(c => {
            const platforms = getUniquePlatforms(c.ad_accounts);
            const isActive = activeClientId === String(c.id);
            const isHidden = hiddenClients.includes(c.id);
            return (
              <div key={c.id}
                onClick={() => editingVisibility ? toggleHidden(c.id) : handleClientClick(c.id)}
                style={{
                  padding: '6px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  borderRadius: 6,
                  margin: '0 6px',
                  background: isActive && !editingVisibility ? colors.activeBg : 'transparent',
                  opacity: editingVisibility && isHidden ? 0.4 : 1,
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!isActive || editingVisibility) e.currentTarget.style.background = colors.hoverBg; }}
                onMouseLeave={e => { if (!isActive || editingVisibility) e.currentTarget.style.background = isActive && !editingVisibility ? colors.activeBg : 'transparent'; }}
              >
                {editingVisibility ? (
                  <div style={{
                    width: 14, height: 14, borderRadius: 3,
                    border: `1.5px solid ${isHidden ? colors.border : '#7F77DD'}`,
                    background: isHidden ? 'transparent' : '#7F77DD',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {!isHidden && <svg width="8" height="7" viewBox="0 0 8 7" fill="none"><path d="M1 3.5L3 5.5L7 1" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                ) : (
                  <div style={{
                    width: 4, height: 4, borderRadius: '50%', flexShrink: 0,
                    background: isActive ? '#7F77DD' : colors.border,
                  }} />
                )}

                <span style={{
                  fontSize: 13, fontWeight: 400,
                  color: isActive && !editingVisibility ? colors.activeText : colors.text2,
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{c.name}</span>

                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                  {platforms.includes('meta') && (
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: colors.meta.bg, color: colors.meta.color, fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>M</div>
                  )}
                  {platforms.includes('google') && (
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: colors.google.bg, color: colors.google.color, fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>G</div>
                  )}
                  {platforms.includes('tiktok') && (
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: colors.tiktok.bg, color: colors.tiktok.color, fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>T</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Impostazioni */}
      <div style={{ borderTop: `1px solid ${colors.border}`, padding: '10px 10px 4px', flexShrink: 0 }}>
        <div style={{ padding: '0 10px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: colors.text3 }}>
          Impostazioni
        </div>

        <NavLink to="/settings/clients" style={({ isActive }) => navLinkStyle(isActive)} onClick={handleNavClick}
          onMouseEnter={e => e.currentTarget.style.background = colors.hoverBg}
          onMouseLeave={e => { const a = e.currentTarget.querySelector('[data-active]'); if (!a) e.currentTarget.style.background = 'transparent'; }}
        >
          {({ isActive }) => <>
            {isActive && <div data-active style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 14, borderRadius: 2, background: '#7F77DD' }} />}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={isActive ? '#7F77DD' : 'currentColor'} strokeWidth="1.3" style={{ flexShrink: 0 }}>
              <circle cx="7" cy="5" r="2.5"/><path d="M2 12c0-2.2 2.2-4 5-4s5 1.8 5 4" strokeLinecap="round"/>
            </svg>
            <span>Clienti</span>
          </>}
        </NavLink>

        <NavLink to="/settings/integrations" style={({ isActive }) => navLinkStyle(isActive)} onClick={handleNavClick}
          onMouseEnter={e => e.currentTarget.style.background = colors.hoverBg}
          onMouseLeave={e => { const a = e.currentTarget.querySelector('[data-active]'); if (!a) e.currentTarget.style.background = 'transparent'; }}
        >
          {({ isActive }) => <>
            {isActive && <div data-active style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 14, borderRadius: 2, background: '#7F77DD' }} />}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={isActive ? '#7F77DD' : 'currentColor'} strokeWidth="1.3" style={{ flexShrink: 0 }}>
              <rect x="1.5" y="5" width="3" height="3" rx="1"/><rect x="9.5" y="5" width="3" height="3" rx="1"/><line x1="4.5" y1="6.5" x2="9.5" y2="6.5"/>
            </svg>
            <span>Integrazioni</span>
          </>}
        </NavLink>

        <NavLink to="/settings/team" style={({ isActive }) => navLinkStyle(isActive)} onClick={handleNavClick}
          onMouseEnter={e => e.currentTarget.style.background = colors.hoverBg}
          onMouseLeave={e => { const a = e.currentTarget.querySelector('[data-active]'); if (!a) e.currentTarget.style.background = 'transparent'; }}
        >
          {({ isActive }) => <>
            {isActive && <div data-active style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 14, borderRadius: 2, background: '#7F77DD' }} />}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={isActive ? '#7F77DD' : 'currentColor'} strokeWidth="1.3" style={{ flexShrink: 0 }}>
              <circle cx="5" cy="4.5" r="2"/><path d="M1 12c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5" strokeLinecap="round"/><circle cx="10" cy="4.5" r="1.5"/><path d="M12 11c0-1.5-1-2.5-2-3" strokeLinecap="round"/>
            </svg>
            <span>Team</span>
          </>}
        </NavLink>
      </div>

      {/* Footer: profilo utente */}
      {user && (
        <div style={{
          padding: '10px 14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderTop: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: colors.activeBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600, color: colors.activeText,
            flexShrink: 0, overflow: 'hidden',
          }}>
            {user.user_metadata?.avatar_url
              ? <img src={user.user_metadata.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : getInitials(user.email || 'U')}
          </div>
          <span style={{ fontSize: 12, color: colors.text3, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email?.split('@')[0]}
          </span>
          <button onClick={signOut} title="Esci" style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text3, padding: 2, display: 'flex', alignItems: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
              <path d="M9 7H2M5 4l-3 3 3 3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 2h4a1 1 0 011 1v8a1 1 0 01-1 1H7" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
